import React from "react";
import { Flex, Button, Text } from "@chakra-ui/react";

import { useSegment } from "tracking";
import { Select } from "components/Select";
import { CloseIcon } from "components/SvgIcons";
import { useChatVisibility } from "hooks";

import type { LocaleType } from "lib/apollo/types";

import {
  changeRegion,
  getStorePreference,
  setStorePreference
} from "components/SiteChrome/CountrySelector/CountrySelector.utils";
import { colours } from "@shared/ui.design-system";

import {
  CountryModal,
  CountryModalContent
} from "./CountryRedirectModal.styles";

import { getViewerCountry } from "./CountryRedirectModal.utils";
import { getRegion } from "@shared/utils.locale";

type Option = {
  label: string;
  value: string;
};

const countryOptions: Option[] = [
  { label: "United Kingdom", value: "en-GB" },
  { label: "United States", value: "en-US" }
];

const countryToStore = {
  GB: "UK",
  US: "US"
};

function localeToStore(locale) {
  return countryToStore[locale.split("-")[1]];
}

export const CountryRedirectModal: React.FC = () => {
  const { track } = useSegment();

  const [selectedCountry, setSelectedCountry] = React.useState<Option>(
    countryOptions[0]
  );
  const [isVisible, setIsVisible] = React.useState(false);

  const region = getRegion();

  React.useEffect(() => {
    const flag = document.getElementById("flag-selector");
    const onClick = () => setIsVisible(true);

    flag?.addEventListener("click", onClick);

    return () => flag?.removeEventListener("click", onClick);
  }, []);

  React.useEffect(() => {
    (async () => {
      const storePreference = getStorePreference();
      const localStore = await getViewerCountry().then(
        (k) => k && countryToStore[k]
      );
      const userStore = storePreference || localStore;
      const currentStore = (region as string).toUpperCase();

      if (!userStore || userStore === currentStore) return;

      const userCountry = countryOptions.find(
        ({ value }) => localeToStore(value as LocaleType) === userStore
      );

      if (userCountry) setSelectedCountry(userCountry as Option);
      setIsVisible(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useChatVisibility(isVisible);

  const dismissPopup = () => {
    setIsVisible(false);
    setStorePreference(selectedCountry.value as LocaleType);
  };

  return (
    <>
      {isVisible && (
        <CountryModal id="country-modal" data-testid="country-redirect-popup">
          <CountryModalContent>
            <CloseIcon
              h="20px"
              w="20px"
              position="absolute"
              top="14px"
              right="24px"
              cursor="pointer"
              onClick={dismissPopup}
            />
            <Text
              textAlign="center"
              fontSize={4}
              lineHeight={2}
              variant="apercu"
              maxW={{ base: "200px", sm: "unset" }}
            >
              Where should we deliver to?
            </Text>
            <Flex
              alignItems="center"
              mt="18px"
              justifyContent="flex-start"
              width={{ base: "100%", lg: "380px" }}
              maxW="380px"
            >
              <Select
                items={countryOptions}
                checkIcon
                testId="quantity-selector"
                onChange={setSelectedCountry}
                selected={selectedCountry}
                minWidth="100%"
                containerStyles={{
                  width: "100%",
                  height: "2.5rem",
                  backgroundColor: colours.secondary.light,
                  "& button": {
                    height: "2.5rem"
                  }
                }}
                menuStyles={{
                  borderRadius: 0,
                  border: `1px solid ${colours.primary.medium}`,
                  padding: "0.5rem 14px"
                }}
                textProps={{
                  variant: "acuminLight",
                  color: "black"
                }}
              />
              <Button
                mt={0}
                ml={1}
                minH="2.5rem !important"
                w="5.625rem"
                variant="primary"
                onClick={async () => {
                  if (
                    region?.toUpperCase() ===
                    localeToStore(selectedCountry.value as LocaleType)
                  ) {
                    dismissPopup();
                    return;
                  }
                  track({
                    event: "Store Selector Click",
                    properties: {
                      country_selected: selectedCountry.label
                    }
                  });
                  await changeRegion(selectedCountry.value as LocaleType);
                }}
              >
                OK
              </Button>
            </Flex>
          </CountryModalContent>
        </CountryModal>
      )}
    </>
  );
};
